/**
 * Iter129 — Garantie-Vault (Warranty Tracker)
 *
 * Per-User Garantie-Verwaltung. KI extrahiert Produkt/Hersteller/Garantiedauer
 * aus hochgeladenen Quittungen. Filter: Aktiv (default) / Bald ablaufend /
 * Abgelaufen / Alle. Ampelfarben pro Karte.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import useHighlightParam from "@/hooks/useHighlightParam";
import { DocPreviewDialog } from "@/components/DocPreviewDialog";
import { DocThumb } from "@/components/DocThumb";
import { WarrantyClaimDialog } from "@/components/warranties/WarrantyClaimDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Shield,
  UploadCloud,
  Plus,
  Trash2,
  Edit3,
  Calendar,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Sparkles,
  Paperclip,
  Eye,
  Store,
  Building2,
  Receipt,
  Megaphone,
} from "lucide-react";

const FILTERS = [
  { id: "active", label: "Mit Garantie", color: "emerald" },
  { id: "expiring", label: "Bald ablaufend (30 Tage)", color: "amber" },
  { id: "expired", label: "Abgelaufen", color: "slate" },
  { id: "all", label: "Alle", color: "slate" },
];

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const target = new Date(isoDate + "T23:59:59");
  const now = new Date();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function statusOf(warranty) {
  const days = daysUntil(warranty.expiry_date);
  if (days === null) return { kind: "unknown", label: "Ohne Ablaufdatum", color: "slate" };
  if (days < 0) return { kind: "expired", label: `Abgelaufen vor ${Math.abs(days)} Tagen`, color: "red" };
  if (days <= 30) return { kind: "expiring", label: `Läuft in ${days} Tagen ab`, color: "amber" };
  return { kind: "active", label: `Aktiv (noch ${days} Tage)`, color: "emerald" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH");
  } catch {
    return iso;
  }
}

function fmtPrice(v, c) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)} ${c || "CHF"}`;
}

const EMPTY_FORM = {
  product_name: "",
  manufacturer: "",
  retailer: "",
  serial_number: "",
  purchase_date: "",
  warranty_months: "",
  price: "",
  currency: "CHF",
  notes: "",
  file_id: null,
  file_name: null,
  mime_type: null,
  ai_extracted: false,
};

export default function Warranties() {
  const { company } = useCompany();
  const companyId = company?._id || company?.id;
  const [scope, setScope] = useState("all");
  const [preview, setPreview] = useState(null);
  const [claimFor, setClaimFor] = useState(null);
  const [filter, setFilter] = useState("active");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/warranties?filter=${f}${scope === "company" && companyId ? `&company_id=${companyId}` : ""}`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Garantien konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [scope, companyId]);

  useEffect(() => { load(filter); }, [filter, load]);
  useHighlightParam(items, (w) => w.id);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (w) => {
    setEditId(w.id);
    setForm({
      product_name: w.product_name || "",
      manufacturer: w.manufacturer || "",
      retailer: w.retailer || "",
      serial_number: w.serial_number || "",
      purchase_date: w.purchase_date || "",
      warranty_months: w.warranty_months ?? "",
      price: w.price ?? "",
      currency: w.currency || "CHF",
      notes: w.notes || "",
      file_id: w.file_id || null,
      file_name: w.file_name || null,
      mime_type: w.mime_type || null,
      ai_extracted: !!w.ai_extracted,
    });
    setDialogOpen(true);
  };

  const onExtract = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiClient.post("/warranties/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      setForm((prev) => ({
        ...prev,
        product_name: d.product_name || prev.product_name,
        manufacturer: d.manufacturer || prev.manufacturer,
        purchase_date: d.purchase_date || prev.purchase_date,
        warranty_months: d.warranty_months ?? prev.warranty_months,
        price: d.price ?? prev.price,
        currency: d.currency || prev.currency,
        file_id: d.file_id || null,
        file_name: d.file_name || null,
        mime_type: d.mime_type || null,
        ai_extracted: true,
      }));
      const found = ["product_name", "manufacturer", "purchase_date", "warranty_months", "price"]
        .filter((k) => d[k] != null && d[k] !== "").length;
      toast.success(`KI hat ${found} Feld${found === 1 ? "" : "er"} erkannt. Bitte überprüfen.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Extraktion fehlgeschlagen.");
    } finally {
      setExtracting(false);
    }
  };

  const onDropFiles = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setEditId(null);
      setForm(EMPTY_FORM);
      setDialogOpen(true);
      setTimeout(() => onExtract(file), 50);
    }
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) {
      toast.error("Bitte gib einen Produktnamen ein.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_name: form.product_name.trim(),
        manufacturer: form.manufacturer.trim() || null,
        retailer: (form.retailer || "").trim() || null,
        serial_number: (form.serial_number || "").trim() || null,
        purchase_date: form.purchase_date || null,
        warranty_months: form.warranty_months === "" ? null : parseInt(form.warranty_months, 10),
        price: form.price === "" ? null : parseFloat(form.price),
        currency: form.currency || "CHF",
        notes: form.notes.trim() || null,
      };
      if (editId) {
        await apiClient.put(`/warranties/${editId}`, payload);
        toast.success("Garantie aktualisiert.");
      } else {
        const createPayload = {
          ...payload,
          company_id: companyId || null,
          file_id: form.file_id,
          file_name: form.file_name,
          mime_type: form.mime_type,
          ai_extracted: form.ai_extracted,
        };
        await apiClient.post("/warranties", createPayload);
        toast.success("Garantie gespeichert.");
      }
      setDialogOpen(false);
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Diese Garantie wirklich löschen?")) return;
    try {
      await apiClient.delete(`/warranties/${id}`);
      toast.success("Garantie gelöscht.");
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const counts = useMemo(() => ({ total: items.length }), [items]);

  return (
    <div
      className="space-y-6"
      data-testid="warranties-page"
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDropFiles}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-emerald-600" />
            Garantien
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Quittungen für Garantie-Zwecke ablegen. Die KI erkennt Produkt, Hersteller und Garantiedauer automatisch.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openCreate} data-testid="warranty-new-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1.5" /> Neue Garantie
          </Button>
        </div>
      </div>

      {/* Drop-Hint bei Drag-over */}
      {dragActive && (
        <div className="fixed inset-0 z-50 bg-emerald-500/10 border-4 border-dashed border-emerald-500 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg p-6 shadow-2xl">
            <UploadCloud className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
            <p className="text-lg font-semibold text-slate-900">Beleg fallen lassen — KI extrahiert automatisch</p>
          </div>
        </div>
      )}

      {/* Filter-Tabs */}
      <div className="flex items-center gap-2 flex-wrap border-b border-slate-200 pb-3" data-testid="warranty-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`warranty-filter-${f.id}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              filter === f.id
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        {[["all", "Alle Firmen"], ["company", company?.name || "Diese Firma"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setScope(id)}
            data-testid={`warranty-scope-${id}`}
            className={`px-3 py-1.5 text-xs rounded-full transition-colors inline-flex items-center gap-1 ${
              scope === id ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Building2 className="h-3 w-3" /> {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500" data-testid="warranty-count">
          {counts.total} Eintrag{counts.total === 1 ? "" : "e"}
        </span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Garantien …
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center bg-slate-50 border-dashed border-2 border-slate-300">
          <Shield className="h-10 w-10 mx-auto text-slate-400 mb-3" />
          <p className="text-slate-700 font-medium">
            {filter === "active" ? "Keine aktiven Garantien" :
             filter === "expiring" ? "Keine Garantien laufen demnächst ab" :
             filter === "expired" ? "Keine abgelaufenen Garantien" :
             "Noch keine Garantien gespeichert"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Lade einen Beleg per Drag&amp;Drop hierhin oder klick auf &bdquo;Neue Garantie&ldquo;.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="warranty-list">
          {items.map((w) => {
            const st = statusOf(w);
            const colorClasses = {
              emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
              amber: "bg-amber-50 border-amber-200 text-amber-900",
              red: "bg-red-50 border-red-200 text-red-900",
              slate: "bg-slate-50 border-slate-200 text-slate-700",
            }[st.color];
            const Icon = st.color === "emerald" ? CheckCircle2 :
                         st.color === "amber" ? AlertTriangle :
                         st.color === "red" ? XCircle : Calendar;
            return (
              <Card key={w.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`warranty-card-${w.id}`} data-highlight-id={w.id}>
                <div className={`rounded-md border px-2 py-1.5 text-xs font-medium flex items-center gap-1.5 mb-3 ${colorClasses}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {st.label}
                </div>
                <div className="flex gap-3">
                {(w.has_file || w.file_id) && (
                  <DocThumb url={`/warranties/${w.id}/file`} title={w.file_name || w.product_name} onOpen={() => setPreview(w)} className="h-20 w-20" testid={`warranty-thumb-${w.id}`} />
                )}
                <div className="space-y-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 leading-tight">{w.product_name}</h3>
                  {w.manufacturer && (
                    <p className="text-sm text-slate-600">{w.manufacturer}</p>
                  )}
                  {w.retailer && (
                    <p className="text-xs text-slate-600 inline-flex items-center gap-1" data-testid={`warranty-retailer-${w.id}`}><Store className="h-3 w-3" /> Gekauft bei {w.retailer}</p>
                  )}
                  {w.company_name && (
                    <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {w.company_name}</p>
                  )}
                </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-slate-500">Kaufdatum</div>
                    <div className="font-medium text-slate-800">{fmtDate(w.purchase_date)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Garantie</div>
                    <div className="font-medium text-slate-800">
                      {w.warranty_months ? `${w.warranty_months} Monate` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500">Läuft ab</div>
                    <div className="font-medium text-slate-800">{fmtDate(w.expiry_date)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Preis</div>
                    <div className="font-medium text-slate-800">{fmtPrice(w.price, w.currency)}</div>
                  </div>
                </div>
                {w.notes && (
                  <p className="mt-2 text-xs text-slate-600 italic border-l-2 border-slate-200 pl-2">{w.notes}</p>
                )}
                {w.claim?.reported_at && (
                  <div className="mt-2 inline-flex items-center gap-1 rounded border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] text-orange-900" data-testid={`warranty-claim-status-${w.id}`} title={`An ${w.claim.to}: ${w.claim.subject || ""}`}>
                    <Megaphone className="h-3 w-3" /> Garantiefall gemeldet am {fmtDate(w.claim.reported_at)}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    {w.ai_extracted && <Sparkles className="h-3 w-3 text-violet-500" title="KI-extrahiert" />}
                    {(w.has_file || w.file_id) && (
                      <button
                        type="button"
                        onClick={() => setPreview(w)}
                        className="hover:text-slate-900 inline-flex items-center gap-1"
                        title={w.file_name || "Garantiebeleg ansehen"}
                        data-testid={`warranty-file-link-${w.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> Ansehen
                      </button>
                    )}
                    {(w.receipt_id || w.bill_id) && (
                      <Link
                        to={w.receipt_id ? `/receipts?highlight=${w.receipt_id}` : `/bills?highlight=${w.bill_id}`}
                        className="hover:text-slate-900 inline-flex items-center gap-1 text-emerald-700"
                        title="Zum verbuchten Kaufbeleg in der Buchhaltung springen"
                        data-testid={`warranty-receipt-link-${w.id}`}
                      >
                        <Receipt className="h-3.5 w-3.5" /> Kaufbeleg
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setClaimFor(w)} className="h-7 px-2 text-xs border-orange-300 text-orange-800 hover:bg-orange-50" data-testid={`warranty-claim-${w.id}`} title="Reklamations-E-Mail an den Händler mit Kaufbeleg im Anhang erzeugen">
                      <Megaphone className="h-3.5 w-3.5 mr-1" /> Garantiefall
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(w)} data-testid={`warranty-edit-${w.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(w.id)} data-testid={`warranty-delete-${w.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <DocPreviewDialog open={!!preview} onClose={() => setPreview(null)} title={preview?.file_name || preview?.product_name} mimeType={preview?.mime_type}
        url={preview ? `/warranties/${preview.id}/file` : null} testid="warranty-preview" />
      <WarrantyClaimDialog warranty={claimFor} onClose={() => setClaimFor(null)} onSent={() => load(filter)} />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="warranty-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              {editId ? "Garantie bearbeiten" : "Neue Garantie"}
            </DialogTitle>
          </DialogHeader>

          {!editId && (
            <div
              className="rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 p-5 text-center cursor-pointer hover:bg-emerald-100 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="warranty-extract-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => onExtract(e.target.files?.[0])}
                data-testid="warranty-extract-input"
              />
              {extracting ? (
                <>
                  <Loader2 className="h-6 w-6 mx-auto text-emerald-600 animate-spin mb-2" />
                  <p className="text-sm font-medium text-emerald-900">KI liest den Beleg …</p>
                </>
              ) : (
                <>
                  <Sparkles className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-sm font-medium text-slate-900">
                    Beleg hochladen — KI füllt die Felder automatisch
                  </p>
                  <p className="text-xs text-slate-600 mt-1">Bild oder PDF · oder einfach unten manuell tippen</p>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label htmlFor="product_name">Produkt *</Label>
              <Input
                id="product_name"
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                placeholder="z.B. iPhone 15 Pro 256GB"
                data-testid="warranty-input-product"
              />
            </div>
            <div>
              <Label htmlFor="manufacturer">Hersteller</Label>
              <Input
                id="manufacturer"
                value={form.manufacturer}
                onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                placeholder="z.B. Apple"
                data-testid="warranty-input-manufacturer"
              />
            </div>
            <div>
              <Label htmlFor="retailer">Gekauft bei (Händler)</Label>
              <Input
                id="retailer"
                value={form.retailer}
                onChange={(e) => setForm({ ...form, retailer: e.target.value })}
                placeholder="z.B. Digitec"
                data-testid="warranty-input-retailer"
              />
            </div>
            <div>
              <Label htmlFor="serial_number">Seriennummer</Label>
              <Input
                id="serial_number"
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                placeholder="optional"
                data-testid="warranty-input-serial"
              />
            </div>
            <div>
              <Label htmlFor="purchase_date">Kaufdatum</Label>
              <SwissDateField
                id="purchase_date"
                
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                data-testid="warranty-input-purchase-date"
              />
            </div>
            <div>
              <Label htmlFor="warranty_months">Garantie (Monate)</Label>
              <Input
                id="warranty_months"
                type="number"
                min="0"
                value={form.warranty_months}
                onChange={(e) => setForm({ ...form, warranty_months: e.target.value })}
                placeholder="24"
                data-testid="warranty-input-months"
              />
            </div>
            <div>
              <Label htmlFor="price">Preis</Label>
              <div className="flex gap-2">
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="1299.00"
                  data-testid="warranty-input-price"
                />
                <Input
                  className="w-20"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  placeholder="CHF"
                  data-testid="warranty-input-currency"
                />
              </div>
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional: Seriennummer, Kaufort, Service-Hotline …"
                rows={2}
                data-testid="warranty-input-notes"
              />
            </div>
            {form.file_name && (
              <div className="col-span-2 text-xs text-slate-600 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Hochgeladen: {form.file_name}
                {form.ai_extracted && <Badge className="ml-2 bg-violet-100 text-violet-800">KI</Badge>}
              </div>
            )}
            {form.purchase_date && form.warranty_months && (
              <div className="col-span-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs px-3 py-2">
                Berechnetes Ablaufdatum: <strong>{(() => {
                  const d = new Date(form.purchase_date);
                  d.setMonth(d.getMonth() + parseInt(form.warranty_months, 10));
                  return d.toLocaleDateString("de-CH");
                })()}</strong>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="warranty-cancel-btn">
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="warranty-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {editId ? "Speichern" : "Garantie anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
